/*
 * Copyright (C) Serfim TIC
 * All rights reserved
 */
class NavigationService {
    /** @ngInject */
    constructor($rootScope, $q, $state, $log, configuration, authenticationService, notificationService) {
        this.$rootScope = $rootScope;
        this.$q = $q;
        this.$state = $state;
        this.$log = $log;
        this.configuration = configuration;
        this.authenticationService = authenticationService;
        this.notificationService = notificationService;
        this.authenticatedUser = null;
        // Listens to HTTP unauthorized events emitted by the interceptor.
        this.$rootScope.$on(configuration.UNAUTHORIZED_EVENT_NAME, () => this.handleUnauthorizedEvent());
        this.$rootScope.$on(configuration.FORBIDDEN_EVENT_NAME, () => this.handleForbiddenEvent());
        this.$rootScope.$on(configuration.UNEXPECTED_STATUS_EVENT_NAME,
                (event, response) => this.handleHttpUnexpectedStatus(event, response));
        this.$log.debug('Service \'Navigation\' loaded');
    }

    /**
     * Validates a UI-router transition by ensuring the user has the excepted permissions.
     *
     * @param {Transition} transition A UI-router transition.
     * @return {HookResult} true if the transition is allowed, or a TargetState object if the transition is denied. See
     * UI-router for an explanation of all possible results.
     */
    validateTransition(transition) {
        const forbiddenResult = this.isStateDenied(transition, transition.$to());
        if (forbiddenResult) {
            this.$log.debug(`Transition forbidden`);
        }

        let result;
        if (forbiddenResult === true) {
            // If transition to the state is forbidden, redirect to the dedicated view.
            result = this.$state.target('gmao.forbidden');
        } else if (forbiddenResult === false) {
            // Transition is allowed.
            result = true;
        } else {
            // Transition is forbidden, but the target state is provided by the 'isStateDenied' method.
            result = forbiddenResult;
        }
        return result;
    }

    /**
     * Tells whether the transition to the state is denied. The transition is denied if:
     * <ul>
     * <li>The user is not authenticated.</li>
     * <li>The target state has restricted access, and the user has not the permissions required.</li>
     * <li>The target state has no restricted access, but a parent state has and the user has not the permissions
     * required.</li>
     * </ul>
     *
     * @param {Transition} transition The origin UI-router transition.
     * @param {State} state A state object.
     */
    isStateDenied(transition, state) {
        let forbidden;
        if (state.data && state.data.permissions && state.data.permissions.only) {
            if (this.authenticationService.isUserAuthenticated()) {
                forbidden = !this.authenticationService.userHasRole(state.data.permissions.only);
            } else {
                forbidden = this.handleUnauthorizedEvent(transition);
            }
        } else if (state.parent) {
            forbidden = this.isStateDenied(transition, state.parent);
        } else {
            forbidden = false;
        }
        return forbidden;
    }

    /**
     * Handles failed authentication events, and activates the 'login' state.
     *
     * @param {Object} [transition] Transition that may be in progress.
     * @param {Object} event The HTTP unauthorized event.
     * @return {Object} A new target state if there was a transition in progress.
     */
    handleUnauthorizedEvent(transition) {
        let target;
        // User is not authenticated, so he is redirected to the login page.
        // However, we must fix the state he was trying to reach when the authentication error occured, so as we can
        // redirect him to this state, in case of successful authentication.
        if (this.$state.current.name === 'login') {
            // On login page, the target state is already predefined in the state parameters.
            target = this.$state.params.target;
        } else {
            this.notificationService.pushMessage('authentication.sessionexpired',
                this.configuration.NOTIFIER_LEVEL_ERROR);

            // On other page, the target state is the current state (copy params to keep read-only).
            target = {
                name: this.$state.current.name,
                params: angular.copy(this.$state.params)
            }
        }

        this.authenticationService.clearSession();
        let targetState;
        if (transition) {
            targetState = this.$state.target('login', {target});
        } else {
            this.$state.go('login', {target});
        }
        return targetState;
    }

    /**
     * Handles failed authorization events, and notifies the user.
     */
    handleForbiddenEvent() {
        this.$state.go('gmao.forbidden');
    }

    /**
     * Handles unexpected HTTP statuses, and notifies the user.
     */
    handleHttpUnexpectedStatus(event, response) {
        this.notificationService.pushMessage(`server.error.${response.status}`,
            this.configuration.NOTIFIER_LEVEL_ERROR);
    }
}

export { NavigationService };



// WEBPACK FOOTER //
// ./src/main/front/scripts/module/security/service/NavigationService.js